<script setup lang="ts">
import { useField, useForm, useIsFormDirty, useIsFormValid } from "vee-validate";
import * as yup from "yup";

import { ALERT_STATUS } from "~/common/interfaces/alert-status.enum";
import type { UpdateCustomerByIdDto } from "~/common/interfaces/dto/customers/update-customer-by-id.dto";
import { useAlertStore } from "~/stores/alert";
import { useCustomersStore } from "~/stores/customers";
import { useModalStore } from "~/stores/modal";

const { t } = useI18n();
const alertStore = useAlertStore();
const customersStore = useCustomersStore();
const { customersModalEdit } = useModalStore();
const route = useRoute();
const { errors, handleSubmit } = useForm({
	initialValues: {
		email: customersModalEdit.customer.email,
		firstName: customersModalEdit.customer.firstName,
		lastName: customersModalEdit.customer.lastName,
		phoneNumber: customersModalEdit.customer.phoneNumber
	},
	validationSchema: yup.object(
		{
			email: yup
				.string()
				.email(t("INPUT.email.error.email"))
				.required(t("INPUT.email.error.required")),
			firstName: yup
				.string()
				.required(t("INPUT.firstName.error.required")),
			lastName: yup
				.string()
				.required(t("INPUT.lastName.error.required")),
			phoneNumber: yup
				.string()
				.required(t("INPUT.phoneNumber.error.required"))
		}
	)
});
const isFormDirty = useIsFormDirty();
const isFormValid = useIsFormValid();

const onSubmit = handleSubmit(async (dto: UpdateCustomerByIdDto) => {
	customersStore.currentCustomer = await customersStore.patchById(customersModalEdit.customer.id, dto);

	customersModalEdit.closeModal();

	if (route.name === "customers") {
		customersStore.getAll(customersStore.recentGetAllQuery);
	}

	alertStore.addAlert({
		title: t("COMPONENTS.CUSTOMERS.MODAL_EDIT.alert.title"),
		description: t("COMPONENTS.CUSTOMERS.MODAL_EDIT.alert.description", { email: customersModalEdit.customer.email }),
		status: ALERT_STATUS.SUCCESS
	});
});

const { value: firstName } = useField<string>("firstName");
const { value: lastName } = useField<string>("lastName");
const { value: email } = useField<string>("email");
const { value: phoneNumber } = useField<string>("phoneNumber");
</script>

<template>
	<AppModal
		:title="$t('COMPONENTS.CUSTOMERS.MODAL_EDIT.title')"
		:close-button-text="$t('COMMON.cancel')"
		:submit-button-text="$t('COMMON.save')"
		:submit-button-active="isFormValid && isFormDirty"
		@close="customersModalEdit.closeModal"
		@submit="onSubmit"
	>
		<div class="flex flex-col gap-y-4">
			<div>
				<span class="font-semibold text-base">
					{{ $t('COMMON.generalInfo') }}
				</span>
				<div class="mt-4 flex flex-col gap-y-2">
					<AppInput
						v-model="email"
						type="email"
						:title="$t('COMMON.email')"
						:placeholder="$t('INPUT.PLACEHOLDER.email')"
						:error="errors.email"
					/>
					<div class="flex gap-x-2">
						<AppInput
							v-model="firstName"
							type="text"
							:title="$t('COMMON.firstName')"
							:placeholder="$t('INPUT.PLACEHOLDER.firstName')"
							:error="errors.firstName"
						/>
						<AppInput
							v-model="lastName"
							type="text"
							:title="$t('COMMON.lastName')"
							:placeholder="$t('INPUT.PLACEHOLDER.lastName')"
							:error="errors.lastName"
						/>
					</div>
					<AppInput
						v-model="phoneNumber"
						type="tel"
						:title="$t('COMMON.phoneNumber')"
						:placeholder="$t('INPUT.PLACEHOLDER.phoneNumber')"
						:error="errors.phoneNumber"
					/>
				</div>
			</div>
		</div>
	</AppModal>
</template>
